import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSalesAnalytics } from "../../../hooks/useAnalytics";
import { AnalyticsFilter } from "../../../types/analytics";
import PeriodSelector from "../../../components/backend/analytics/PeriodSelector";
import RevenueChart from "../../../components/backend/analytics/RevenueChart";

const RevenueTimelinePage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<AnalyticsFilter>({ period: "30d" });
  const { data: response, isLoading } = useSalesAnalytics(filter);
  const sales = response?.data;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          {t("admin.analytics.revenueTimeline")}
        </Typography>
        <PeriodSelector filter={filter} onChange={setFilter} />
      </Box>

      {sales && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    ${sales.totalRevenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.analytics.totalRevenue")} (USD)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="h4" fontWeight={700} color="secondary.main">
                    {sales.totalOrders}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.analytics.totalOrders")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    ${sales.averageOrderValue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.analytics.avgOrderValue")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Full-width Revenue + Orders chart */}
          <RevenueChart
            title={t("admin.analytics.revenueOverTime")}
            revenueData={sales.revenueTimeline}
            orderCountData={sales.orderCountTimeline}
            height={450}
            showOrderCount
          />
        </>
      )}
    </Box>
  );
};

export default RevenueTimelinePage;
